<script lang="ts" setup>
import { BaseButton } from '@qic/ui-components';
import { Vue3Lottie } from 'vue3-lottie';
import { useRtl } from '@shared/composables';
import { BasePopup } from '@shared/ui';

const route = useRoute();
const { isRtl } = useRtl();

interface Item {
  id: number;
  title: string;
  description: string;
  is_marked: boolean;
  full_width_block: boolean;
  button: {
    id: number;
    button?: Button;
    modal?: Popup;
  };
  lottie_json?: {
    documentId: string,
    id: number,
    mime: string,
    url: string,
  };
  picture?: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
}

interface Button {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  href: string;
  target: string;
  icon_position: string;
  icon?: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
}

interface Popup {
  id: number;
  documentId: string;
  title: string;
  subtitle: string;
  description: string;
  content: string;
  is_extended: boolean;
  image?: {
    documentId: string,
    height: number,
    id: number,
    mime: string,
    size: number,
    url: string,
    width: number,
  };
}

const props = defineProps<{
  id?: string | number;
  title: string;
  show_indexes: boolean;
  claim_cards: Item[];
}>();

const coveredPopupShown = ref<boolean>(false);
const currentPopupInfo = ref<Popup>({} as Popup);
const showCoveredPopup = (popup: Popup | undefined) => {
  currentPopupInfo.value = popup || ({} as Popup);
  coveredPopupShown.value = true;
};
const closeCoveredPopup = () => {
  coveredPopupShown.value = false;
};

const lottieJson = ref<{ [key: string | number]: string }>({});

async function callLottieFunction (jsonUrl: string) {
  const { data, error } = await useFetch(`${window.location.origin}${route.path.replace(/\/$/, '')}/api/fetchJson`, {
    params: { url: jsonUrl },
  });
  if (data.value) {
    return data.value;
  } else {
    console.error('Error loading JSON: ', error);
    return false;
  }
}

const checkLottieJson = async (cards: any) => {
  for (const item of cards) {
    if (item.lottie_json) {
      try {
        lottieJson.value[item.lottie_json.id] = await callLottieFunction(item.lottie_json.url);
      } catch (error) {
        console.error('Error loading JSON:', error);
      }
    }
  }
};

onMounted(async () => {
  await nextTick();
  checkLottieJson(props.claim_cards);
});
</script>

<template>
  <section class="step-claim section-offset" dir="auto">
    <div class="container">
      <div class="step-claim__head">
        <h3>{{ props.title }}</h3>
      </div>
      <div class="step-claim__wrapper">
        <div
          v-for="(item, key) in props.claim_cards" class="step-claim__content"
          :class="{ 'is_marked': item.is_marked, 'full_width': item.full_width_block, 'height_m': !props.show_indexes, 'lottie_wrapper': item.lottie_json }"
        >
          <div v-if="item.lottie_json || (item.picture && item.picture.url)" class="picture">
            <div v-if="item.lottie_json" :class="{ 'loading': !lottieJson[item.lottie_json.id] }">
              <Vue3Lottie
                v-if="item.lottie_json && lottieJson[item.lottie_json.id]"
                :animation-data="lottieJson[item.lottie_json.id]" class="animation"
              />
            </div>
            <img v-else-if="item.picture?.url" :src="item.picture?.url">
          </div>
          <div v-if="!item.lottie_json" class="row-group">
            <h3 class="title">
              {{ item.title }}
            </h3>
            <div v-if="item.description" class="description" v-html="item.description" />
            <div v-if="item.button && item.button.button" class="button">
              <div v-if="item.button.modal" class="btn dc-link-button" @click="showCoveredPopup(item.button.modal)">
                <img v-if="item.button.button.icon" :src="item.button.button.icon.url">
                {{ item.button.button.title }}
              </div>
              <a v-else :href="item.button.button.href" :target="item.button.button.target">
                <BaseButton medium icon="none" class="dc-link-button">
                  <span v-if="item.button.button.icon && item.button.button.icon_position === 'left'" class="icon-left">
                    <img :src="item.button.button.icon.url">
                  </span>
                  {{ item.button.button.title }}
                  <span
                    v-if="item.button.button.icon && item.button.button.icon_position === 'right'"
                    class="icon-right"
                  >
                    <img :src="item.button.button.icon.url">
                  </span>
                </BaseButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <BasePopup
    v-if="coveredPopupShown" big :popup-v2="true" :is-rtl="isRtl" :use-button="true"
    @close="closeCoveredPopup"
  >
    <template #header>
      <div class="landing-includes__popup-header">
        <h4>{{ currentPopupInfo?.title }}</h4>
        <div class="subtitle">
          {{ currentPopupInfo?.description }}
        </div>
        <img v-if="currentPopupInfo?.image" :src="currentPopupInfo.image.url" :alt="currentPopupInfo.title">
      </div>
    </template>

    <div class="landing-includes__popup">
      <h5>{{ currentPopupInfo?.subtitle }}</h5>
      <div v-if="currentPopupInfo?.content" class="content" v-html="currentPopupInfo.content" />
    </div>

    <template #button>
      <BaseButton medium secondary icon="none" class="landing-includes__popup__btn" @click="closeCoveredPopup">
        <span>{{ $t('main.includes.close.button') }}</span>
      </BaseButton>
    </template>
  </BasePopup>
</template>

<style lang="scss" scoped>
@use 'StepsToClaim.scss';
</style>

<style lang="scss">
.step-claim {
  &__content {
    .description {
      ul {
        padding-left: 20px;
        list-style: disc;

        li {
          padding-left: 10px;
          list-style: disc;
          margin-bottom: 16px;
        }
      }
    }
  }
}
</style>
